import React, { FC, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import {
  Router as BrowserRouter,
  Redirect,
  Route,
  Switch,
} from 'react-router-dom'
import configureStore from './configureStore'
import { Provider, useDispatch, useSelector } from 'react-redux'
import {
  transitions,
  positions,
  Provider as AlertProvider,
  useAlert,
} from 'react-alert'
// import AlertTemplate from 'react-alert-template-basic'
import { PersistGate } from 'redux-persist/integration/react'
import history from './utils/history'
import ScrollToTop from './utils/scrollToTop'
import { InitialState as ProfileStateType } from './containers/SignInPage/types'
import * as profileActions from './containers/SignInPage/actions'
import * as Sentry from '@sentry/react'
import { ToastContainer } from 'react-toastify'
import Confetti from 'react-confetti'
import { checkProfileFilled, checkProfileRole } from './utils/helpers'
import notification from './components/Notification'
import Cookies from 'js-cookie'
import ym, { YMInitializer } from 'react-yandex-metrika'

// Components
import ModalQuestionnaire from './components/ModalQuestionnaire'
import ModalAchievement from './components/ModalAchievement'

// Theme
import 'react-toastify/dist/ReactToastify.min.css'
import './theme/styles.scss'

// Site pages
import HandleCode from './containers/HandleCode'
import SignInPage from './containers/SignInPage'
import MainPage from './containers/MainPage'
import CoursesListPage from './containers/CoursesListPage'
import CoursePage from './containers/CoursePage'
import ModulePage from './containers/ModulePage'
import ModuleScreenPage from './containers/ModuleScreenPage'
import ModuleLessonPage from './containers/LessonPage'
import NotionPage from './containers/NotionPage'
import VideoPage from './containers/VideoPage'
import ProfilePage from './containers/ProfilePage'
import ProfileParentsPage from './containers/ProfileParentsPage'
import ProfileTeachersPage from './containers/ProfileTeachersPage'
import ProfileCoursesPage from './containers/ProfileCoursesPage'
import ProfileContestsPage from './containers/ProfileContestsPage'
import ProfileExpertCabinetPage from './containers/ProfileExpertCabinetPage'
import ProfileContestsWorkPage from './containers/ProfileContestsWorkPage'
import ProfileContestsWorksListPage from './containers/ProfileContestsWorksListPage'
import ProfileAchievementsPage from './containers/ProfileAchievementsPage'
import ProfileAwardsPage from './containers/ProfileAwardsPage'
import NotFoundPage from './containers/NotFoundPage'

// Admin pages
import AdminPage from './containers/AdminPage'
import AdminCoursesPage from './containers/AdminCoursesListPage'
import AdminCourseCreatePage from './containers/AdminCourseCreatePage'
import AdminCourseEditPage from './containers/AdminCourseEditPage'
import AdminLessonsListPage from './containers/AdminLessonsListPage'
import AdminLessonCreatePage from './containers/AdminLessonCreatePage'
import AdminLessonEditPage from './containers/AdminLessonEditPage'
import AdminLessonBlocksPage from './containers/AdminLessonBlocksPage'
import AdminModuleCreatePage from './containers/AdminModuleCreatePage'
import AdminModuleEditPage from './containers/AdminModuleEditPage'
import AdminModulesListPage from './containers/AdminModulesListPage'
import AdminModuleScreenBlocks from './containers/AdminModuleScreenBlocks'
import AdminBlockEditPage from './containers/AdminBlockEditPage'
import AdminMapEditPage from './containers/AdminMapEditPage'
import AdminAchievementsPage from './containers/AdminAchievementsPage'
import AdminAchievementEditPage from './containers/AdminAchievementEditPage'
import AdminUsersPage from './containers/AdminUsersPage'
import AdminUserViewPage from './containers/AdminUserViewPage'

const { REACT_APP_YANDEX_METRIKA_ID, REACT_APP_SENTRY_DSN, REACT_APP_ENV } =
  process.env

type PrivateRouteType = {
  component: FC
  isAuthed: boolean
  exact?: boolean
  path: string
}

type PrivateAdminRouteType = {
  component: FC
  isAdministrator: boolean
  exact?: boolean
  path: string
}

export const { store, persistor } = configureStore()

Sentry.init({
  dsn: REACT_APP_SENTRY_DSN,
  environment: REACT_APP_ENV,
  beforeSend(event, hint) {
    const error = hint?.originalException
    if (
      error &&
      typeof error !== 'string' &&
      error.message &&
      error.message.includes('Network Error')
    ) {
      return null
    }
    return event
  },
})

function PrivateRoute({
  component: Component,
  isAuthed,
  exact,
  ...rest
}: PrivateRouteType): JSX.Element {
  return (
    <Route
      {...rest}
      exact={exact}
      render={() =>
        isAuthed ? <Component /> : <Redirect to={{ pathname: '/signin' }} />
      }
    />
  )
}

function PrivateAdminRoute({
  component: Component,
  isAdministrator,
  exact,
  ...rest
}: PrivateAdminRouteType): JSX.Element {
  return (
    <Route
      {...rest}
      exact={exact}
      render={() => (isAdministrator ? <Component /> : <NotFoundPage />)}
    />
  )
}

function Router() {
  const { innerWidth: width, innerHeight: height } = window

  const [confettiProcess, setConfettiProcess] = useState<boolean>(false)

  const dispatch = useDispatch()

  const alert = useAlert()

  const {
    profile,
    newAchievements,
    selectedAchievement,
    error,
    modalQuestionnaireIsOpen,
  } = useSelector((state: { profile: ProfileStateType }) => state.profile)

  const token = Cookies.get('token')

  const isAuthed = !!(profile && profile.user_id)

  const isAdministrator = checkProfileRole(profile?.roles, 'administrator')
  const isExpert = checkProfileRole(profile?.roles, 'expert')

  useEffect(() => {
    if (isAuthed || token) {
      dispatch(profileActions.fetchProfile())
      dispatch(profileActions.setSelectedAchievement(null))
    }
  }, [isAuthed, token])

  useEffect(() => {
    if (isAuthed && newAchievements) {
      newAchievements.forEach(achievement => {
        notification(
          achievement.image_media?.url,
          achievement.description,
          achievement.caption,
          () => {
            dispatch(
              profileActions.markViewedAchievement(achievement.achievement_id)
            )
          },
          () => null,
          () => dispatch(profileActions.setSelectedAchievement(achievement)),
          achievement.achievement_id
        )
        setConfettiProcess(true)
      })
    }
  }, [newAchievements])

  useEffect(() => {
    if (error) {
      alert.error(error)
    }
  }, [error])

  useEffect(() => {
    if (REACT_APP_YANDEX_METRIKA_ID) {
      ym('hit', history.location.pathname)
      return history.listen(location => ym('hit', location.pathname))
    }
  }, [history])

  useEffect(() => {
    if (profile && !checkProfileFilled(profile)) {
      const pathParts = history.location.pathname.split('/')
      if (pathParts[1] === 'courses' && pathParts[2] && profile) {
        dispatch(profileActions.checkFirstModuleComplete(pathParts[2]))
      }

      return history.listen(location => {
        const parts = location.pathname.split('/')
        if (parts[1] === 'courses' && parts[2] && profile) {
          dispatch(profileActions.checkFirstModuleComplete(parts[2]))
        }
      })
    }
  }, [history, profile])

  useEffect(() => {
    if (profile) {
      const { user_id, external_profile, email, roles } = profile
      Sentry.setUser({
        id: user_id,
        username: external_profile.username,
        email,
      })
      Sentry.setContext('userInfo', {
        email,
        first_name: profile.profile.first_name,
        last_name: profile.profile.last_name,
        phone: profile.profile.contact_phone,
        role: Array.isArray(roles) && roles.length ? roles[0].name : undefined,
      })
    } else {
      Sentry.configureScope(scope => scope.setUser(null))
    }
  }, [profile])

  return (
    <BrowserRouter history={history}>
      <ScrollToTop />
      <Switch>
        <Route path={'/signin'} component={SignInPage} />
        <Route exact={true} path={'/notion-renderer'} component={NotionPage} />
        <Route exact={true} path={'/video'} component={VideoPage} />
        <Route exact={true} path={'/'} component={MainPage} />
        <Route exact={true} path={'/handle-code'} component={HandleCode} />
        <Route exact={true} path={'/courses'} component={CoursesListPage} />
        <Route exact={true} path={'/courses/:id'} component={CoursePage} />
        <PrivateRoute
          exact={true}
          isAuthed={isAuthed}
          path={'/courses/:id/modules/:mid'}
          component={ModulePage}
        />
        <PrivateRoute
          exact={true}
          isAuthed={isAuthed}
          path={'/courses/:id/modules/:mid/screens/:sid'}
          component={ModuleScreenPage}
        />
        <PrivateRoute
          exact={true}
          isAuthed={isAuthed}
          path={'/courses/:id/modules/:mid/lessons/:lid'}
          component={ModuleLessonPage}
        />
        <PrivateRoute
          exact={true}
          component={ProfilePage}
          isAuthed={isAuthed}
          path={'/profile'}
        />
        <PrivateRoute
          exact={true}
          component={ProfileParentsPage}
          isAuthed={isAuthed}
          path={'/profile/parents'}
        />
        <PrivateRoute
          exact={true}
          component={ProfileTeachersPage}
          isAuthed={isAuthed}
          path={'/profile/teachers'}
        />
        <PrivateRoute
          exact={true}
          component={ProfileCoursesPage}
          isAuthed={isAuthed}
          path={'/profile/courses'}
        />
        {isExpert ? (
          <PrivateRoute
            exact={true}
            component={ProfileExpertCabinetPage}
            isAuthed={isAuthed}
            path={'/profile/contests'}
          />
        ) : (
          <PrivateRoute
            exact={true}
            component={ProfileContestsPage}
            isAuthed={isAuthed}
            path={'/profile/contests'}
          />
        )}
        <PrivateRoute
          exact={true}
          component={ProfileContestsWorkPage}
          isAuthed={isAuthed}
          path={'/profile/contests/:id/works/create'}
        />
        <PrivateRoute
          exact={true}
          component={ProfileContestsWorkPage}
          isAuthed={isAuthed}
          path={'/profile/contests/:id/works/:wid'}
        />
        <PrivateRoute
          exact={true}
          component={ProfileContestsWorksListPage}
          isAuthed={isAuthed}
          path={'/contests-works'}
        />
        <PrivateRoute
          exact={true}
          component={ProfileAchievementsPage}
          isAuthed={isAuthed}
          path={'/profile/achievements'}
        />
        <PrivateRoute
          exact={true}
          component={ProfileAwardsPage}
          isAuthed={isAuthed}
          path={'/profile/awards'}
        />
        <PrivateAdminRoute
          exact={true}
          isAdministrator={isAdministrator}
          path={'/admin'}
          component={AdminPage}
        />
        <PrivateAdminRoute
          exact={true}
          isAdministrator={isAdministrator}
          path={'/admin/courses'}
          component={AdminCoursesPage}
        />
        <PrivateAdminRoute
          exact={true}
          isAdministrator={isAdministrator}
          path={'/admin/courses/create'}
          component={AdminCourseCreatePage}
        />
        <PrivateAdminRoute
          exact={true}
          isAdministrator={isAdministrator}
          path={'/admin/courses/:id'}
          component={AdminCourseEditPage}
        />
        <PrivateAdminRoute
          exact={true}
          isAdministrator={isAdministrator}
          path={'/admin/courses/:id/lessons'}
          component={AdminLessonsListPage}
        />
        <PrivateAdminRoute
          exact={true}
          isAdministrator={isAdministrator}
          path={'/admin/courses/:id/lessons/create'}
          component={AdminLessonCreatePage}
        />
        <PrivateAdminRoute
          exact={true}
          isAdministrator={isAdministrator}
          path={'/admin/courses/:id/modules/create'}
          component={AdminModuleCreatePage}
        />
        <PrivateAdminRoute
          exact={true}
          isAdministrator={isAdministrator}
          path={'/admin/courses/:id/lessons/:lid'}
          component={AdminLessonEditPage}
        />
        <PrivateAdminRoute
          exact={true}
          isAdministrator={isAdministrator}
          path={'/admin/courses/:id/modules/:mid/lessons/:lid/blocks'}
          component={AdminLessonBlocksPage}
        />
        <PrivateAdminRoute
          exact={true}
          isAdministrator={isAdministrator}
          path={'/admin/courses/:id/modules'}
          component={AdminModulesListPage}
        />
        <PrivateAdminRoute
          exact={true}
          isAdministrator={isAdministrator}
          path={'/admin/courses/:id/modules/:mid'}
          component={AdminModuleEditPage}
        />
        <PrivateAdminRoute
          exact={true}
          isAdministrator={isAdministrator}
          path={'/admin/courses/:id/modules/:mid/module-screen/:sid/blocks'}
          component={AdminModuleScreenBlocks}
        />
        <PrivateAdminRoute
          exact={true}
          isAdministrator={isAdministrator}
          path={'/admin/courses/:id/modules/:mid/lessons/:lid/blocks/:bid'}
          component={AdminBlockEditPage}
        />
        <PrivateAdminRoute
          exact={true}
          isAdministrator={isAdministrator}
          path={
            '/admin/courses/:id/modules/:mid/module-screen/:sid/blocks/:bid'
          }
          component={AdminBlockEditPage}
        />
        <PrivateAdminRoute
          exact={true}
          isAdministrator={isAdministrator}
          path={'/admin/courses/:id/maps/:map_id'}
          component={AdminMapEditPage}
        />
        <PrivateAdminRoute
          exact={true}
          isAdministrator={isAdministrator}
          path={'/admin/achievements'}
          component={AdminAchievementsPage}
        />
        <PrivateAdminRoute
          exact={true}
          isAdministrator={isAdministrator}
          path={'/admin/achievements/:id'}
          component={AdminAchievementEditPage}
        />
        <PrivateAdminRoute
          exact={true}
          isAdministrator={isAdministrator}
          path={'/admin/users'}
          component={AdminUsersPage}
        />
        <PrivateAdminRoute
          exact={true}
          isAdministrator={isAdministrator}
          path={'/admin/users/:id'}
          component={AdminUserViewPage}
        />
        <Route exact={true} component={NotFoundPage} />
      </Switch>
      <ToastContainer
        position="top-right"
        autoClose={15000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnHover
        style={{ width: '620px', top: 55 }}
        closeButton={false}
      />
      <ModalQuestionnaire
        modal={modalQuestionnaireIsOpen}
        onClose={() => null}
      />
      {confettiProcess ? (
        <Confetti
          width={width}
          height={height}
          style={{ position: 'fixed', top: 0, left: 0, right: 0 }}
          recycle={false}
          onConfettiComplete={() => setConfettiProcess(false)}
        />
      ) : null}
      <ModalAchievement
        achievement={selectedAchievement}
        onClose={() => dispatch(profileActions.setSelectedAchievement(null))}
      />
      {REACT_APP_YANDEX_METRIKA_ID ? (
        <YMInitializer
          accounts={[parseInt(REACT_APP_YANDEX_METRIKA_ID)]}
          options={{
            clickmap: true,
            trackLinks: true,
            accurateTrackBounce: true,
            webvisor: true,
            defer: true,
          }}
        />
      ) : null}
    </BrowserRouter>
  )
}

const optionsAlert = {
  position: positions.BOTTOM_CENTER,
  timeout: 15000,
  offset: '30px',
  transition: transitions.SCALE,
}

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <AlertProvider template={() => null} {...optionsAlert}>
          <Router />
        </AlertProvider>
      </PersistGate>
    </Provider>
  )
}

ReactDOM.render(<App />, document.getElementById('root'))
